import { HoneycombWebSDK } from "@honeycombio/opentelemetry-web"
import { diag, DiagConsoleLogger, DiagLogLevel } from "@opentelemetry/api"
import {
  getWebAutoInstrumentations,
  type InstrumentationConfigMap,
} from "@opentelemetry/auto-instrumentations-web"
import {
  StackContextManager,
  type PropagateTraceHeaderCorsUrls,
  type SpanProcessor,
} from "@opentelemetry/sdk-trace-web"
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_NAMESPACE,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions"

import { RedactingSpanProcessor } from "./redaction"
import {
  setHoneycombUiConfig,
  type HoneycombUiConfig,
} from "./sentry-correlation"
import { UserSpanProcessor } from "./user-span-processor"

const SERVICE_NAMESPACE = "tastyworks"

/** Configuration for {@link initTelemetry}. */
export interface TelemetryConfig {
  /** OTLP/HTTP collector base URL. Empty disables telemetry (the SDK never starts). */
  endpoint: string
  /** `service.name`; must be whitelisted gateway-side before Honeycomb accepts it. */
  serviceName: string
  /** Deterministic 1-in-N sampling (whole number). Undefined = every trace. */
  sampleRate?: number
  /** `service.version`. */
  serviceVersion?: string
  /** `build.tag`. */
  buildTag?: string
  /** `deployment.environment` — Honeycomb segregates traces on it. */
  deploymentEnvironment?: string
  /**
   * Extra static resource attributes (e.g. `telemetry.source`). Core identity
   * attributes (service.name / namespace / version) always take precedence.
   */
  resourceAttributes?: Record<string, string>
  /** User-id getter, read per span so `user.id` appears once the user logs in. */
  getUserId?: () => string | null | undefined
  /**
   * Origins the `traceparent` header may be attached to. Scope to first-party
   * API hosts only — never a wildcard, which leaks trace ids to third parties.
   */
  propagateTraceHeaderCorsUrls?: PropagateTraceHeaderCorsUrls
  /** Honeycomb UI coordinates for the Sentry error → trace deep-link. */
  honeycombUi?: HoneycombUiConfig
  /** Verbose OTEL diag logging. Off in shipped envs. */
  debug?: boolean
}

/**
 * Map a Vite MODE to `deployment.environment`: `live` for prod, `uat`
 * for every other shipped env (collapses staging/uat/cert/…). `live` is IG's
 * name for prod and applies to this attribute only — the Honeycomb environment
 * it lands in is still named `production` (see `honeycombUiForMode`).
 */
export function deploymentEnvironmentForMode(mode: string): string {
  return mode === "production" ? "live" : "uat"
}

/** Assemble OTEL resource attributes, omitting any that are unset. */
export function buildResourceAttributes(
  config: TelemetryConfig
): Record<string, string> {
  const attributes: Record<string, string> = {
    ...config.resourceAttributes,
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_NAMESPACE]: SERVICE_NAMESPACE,
  }
  if (config.serviceVersion !== undefined) {
    attributes[ATTR_SERVICE_VERSION] = config.serviceVersion
  }
  if (config.buildTag !== undefined) {
    attributes["build.tag"] = config.buildTag
  }
  if (config.deploymentEnvironment !== undefined) {
    // `deployment.environment`, not the newer semconv `.name` — Honeycomb keys on this.
    attributes["deployment.environment"] = config.deploymentEnvironment
  }
  return attributes
}

/** Escape a string for literal use inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Build a first-party trace-header allowlist from API base URLs, one RegExp per
 * origin. RegExps (not strings) are required: a string is matched by full-URL
 * equality, which never matches a real request. Each is anchored at the start
 * AND bounded by `/` or end-of-string, so a look-alike host
 * (`api.tastytrade.com.evil.com`) can't match `api.tastytrade.com`.
 * Empty/malformed inputs are skipped and duplicate origins collapsed.
 */
export function corsUrlsFromBaseUrls(
  ...baseUrls: (string | undefined)[]
): RegExp[] {
  const origins = new Set<string>()
  for (const baseUrl of baseUrls) {
    if (!baseUrl) {
      continue
    }
    try {
      origins.add(new URL(baseUrl).origin)
    } catch {
      // Skip malformed URLs rather than break telemetry init.
    }
  }
  return [...origins].map(
    (origin) => new RegExp(`^${escapeRegExp(origin)}(/|$)`)
  )
}

/** Auto-instrumentation config: user-interaction off (noisy); propagation scoped to first-party. */
export function buildWebInstrumentationConfig(
  config: TelemetryConfig
): InstrumentationConfigMap {
  return {
    "@opentelemetry/instrumentation-user-interaction": { enabled: false },
    "@opentelemetry/instrumentation-fetch": {
      propagateTraceHeaderCorsUrls: config.propagateTraceHeaderCorsUrls,
    },
    "@opentelemetry/instrumentation-xml-http-request": {
      propagateTraceHeaderCorsUrls: config.propagateTraceHeaderCorsUrls,
    },
  }
}

/**
 * Initialise browser tracing via the Honeycomb Web SDK (document-load, fetch,
 * XHR, Core Web Vitals) with URL-PII redaction. No-op when no endpoint is set.
 */
export function initTelemetry(config: TelemetryConfig): void {
  if (!config.endpoint) {
    return
  }
  if (config.debug) {
    diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.DEBUG)
  }
  setHoneycombUiConfig(config.honeycombUi)
  // Redactor before the exporter so URL PII is scrubbed pre-export.
  const spanProcessors: SpanProcessor[] = [new RedactingSpanProcessor()]
  if (config.getUserId) {
    spanProcessors.push(new UserSpanProcessor(config.getUserId))
  }
  new HoneycombWebSDK({
    endpoint: config.endpoint,
    serviceName: config.serviceName,
    // Keyless Non-IG gateway: no apiKey, so the SDK's validation must be skipped.
    skipOptionsValidation: true,
    sampleRate: config.sampleRate,
    resourceAttributes: buildResourceAttributes(config),
    // entry_page.* are resource attributes the redactor can't reach; disable the
    // PII-bearing ones at the source (referrer defaults on and can carry a token).
    entryPageAttributes: { url: false, search: false, referrer: false },
    // Avoids pulling in zone.js.
    contextManager: new StackContextManager(),
    spanProcessors,
    // The SDK adds Web Vitals by default — passing one here would double every metric.
    instrumentations: [
      getWebAutoInstrumentations(buildWebInstrumentationConfig(config)),
    ],
    debug: config.debug,
  }).start()
}
